import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Matter, CalendarEvent, CalendarFilters, AppError } from '../types';
import { bigtimeService } from '../services/bigtimeService';
import {
  getStartOfMonth,
  getEndOfMonth,
  formatForApi,
  parseDate,
  isSameDay,
} from '../utils/dateUtils';
import {
  filterMatters,
  extractConsultants,
  extractClients,
  extractLocations,
} from '../utils/dataTransformers';
import { APP_SETTINGS, MATTER_TYPE_CONFIGS, STORAGE_KEYS } from '../constants';

export interface UseCalendarDataReturn {
  // Data
  matters: Matter[];
  events: CalendarEvent[];
  allEvents: CalendarEvent[];
  filteredMatters: Matter[];
  consultants: string[];
  clients: string[];
  locations: string[];

  // State
  currentDate: Date;
  selectedDate: Date | null;
  selectedMatter: Matter | null;
  filters: CalendarFilters;

  // Loading states
  isLoading: boolean;
  isError: boolean;
  error: AppError | null;

  // Actions
  setCurrentDate: (date: Date) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedMatter: (matter: Matter | null) => void;
  setFilters: (filters: Partial<CalendarFilters>) => void;
  refreshData: () => void;
  goToToday: () => void;
  goToNextMonth: () => void;
  goToPreviousMonth: () => void;

  // Event helpers
  getEventsForDate: (date: Date) => CalendarEvent[];
  getMatterById: (id: string) => Matter | undefined;
}

const generateEvents = (matters: Matter[]): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  let mattersWithDates = 0;
  let mattersWithoutDates = 0;

  console.log(`Generating events for ${matters.length} matters`);

  matters.forEach((matter, index) => {
    let hasAnyDate = false;

    // Parse due date and span date first to check the special case
    const dueDate = matter.dueDate && matter.dueDate.trim() !== '' ? parseDate(matter.dueDate) : null;
    const spanDate = matter.spanDate && matter.spanDate.trim() !== '' ? parseDate(matter.spanDate) : null;

    // Check if due date precedes span date
    if (dueDate && spanDate && dueDate < spanDate) {
      // Special case: generate span events for each day from due date to span date (inclusive), excluding weekends
      console.log(`Matter ${index} "${matter.displayName}" has due date (${matter.dueDate}) before span date (${matter.spanDate}) - generating span events for date range (excluding weekends)`);

      const currentDate = new Date(dueDate);
      while (currentDate <= spanDate) {
        // Skip weekends (0 = Sunday, 6 = Saturday) for dates between due date and span date
        const dayOfWeek = currentDate.getDay();
              // Include the first and last date regardless of the day of the week
        if (currentDate.getTime() === dueDate.getTime() || currentDate.getTime() === spanDate.getTime() || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
          events.push({
            matter,
            date: new Date(currentDate),
            dateType: 'span',
            label: 'Span Date',
          });
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      hasAnyDate = true;
    } else {
      // Normal case: handle due date and span date separately
      if (dueDate) {
        console.log(`Matter ${index} "${matter.displayName}" has due date: ${matter.dueDate} -> ${dueDate.toDateString()}`);
        events.push({
          matter,
          date: dueDate,
          dateType: 'due',
          label: 'Due Date',
        });
        hasAnyDate = true;
      }

      if (spanDate) {
        console.log(`Matter ${index} "${matter.displayName}" has span date: ${matter.spanDate} -> ${spanDate.toDateString()}`);
        events.push({
          matter,
          date: spanDate,
          dateType: 'span',
          label: 'Span Date',
        });
        hasAnyDate = true;
      }
    }

    // Additional dates
    matter.additionalDates.forEach((dateStr, dateIndex) => {
      if (dateStr && dateStr.trim() !== '') {
        const date = parseDate(dateStr);
        if (date) {
          console.log(`Matter ${index} "${matter.displayName}" has additional date ${dateIndex + 1}: ${dateStr} -> ${date.toDateString()}`);
          events.push({
            matter,
            date,
            dateType: 'additional',
            label: `Additional Date ${dateIndex + 1}`,
          });
          hasAnyDate = true;
        }
      }
    });

    // Trial presentation dates (if it's a date string, not a person name)
    if (matter.trialPresentation && matter.trialPresentation.trim() !== '' && /\d/.test(matter.trialPresentation)) {
      const date = parseDate(matter.trialPresentation);
      if (date) {
        console.log(`Matter ${index} "${matter.displayName}" has trial presentation date: ${matter.trialPresentation} -> ${date.toDateString()}`);
        events.push({
          matter,
          date,
          dateType: 'trial',
          label: 'Trial Presentation',
        });
        hasAnyDate = true;
      }
    }

    if (hasAnyDate) {
      mattersWithDates++;
    } else {
      mattersWithoutDates++;
      if (index < 5) { // Only log first 5 matters without dates to avoid spam
        console.log(`Matter ${index} "${matter.displayName}" has no dates - dueDate: "${matter.dueDate}", spanDate: "${matter.spanDate}", additionalDates: [${matter.additionalDates.join(', ')}], trialPresentation: "${matter.trialPresentation}"`);
      }
    }
  });

  console.log(`Event generation complete: ${events.length} events from ${mattersWithDates} matters with dates, ${mattersWithoutDates} matters without dates`);
  return events;
};

// Utility functions for filter persistence
const saveFiltersToStorage = (filters: CalendarFilters) => {
  try {
    // Exclude consultant filter from persistence to make it non-sticky
    const { consultant, ...filtersToSave } = filters;
    localStorage.setItem(STORAGE_KEYS.FILTER_STATE, JSON.stringify(filtersToSave));
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

const loadFiltersFromStorage = (): Partial<CalendarFilters> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.FILTER_STATE);
    if (stored) {
      const loadedFilters = JSON.parse(stored);
      // Always reset consultant to 'All' on load
      return { ...loadedFilters, consultant: 'All' };
    }
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
  }
  return null;
};

const getDefaultFilters = (): CalendarFilters => {
  return {
    matterType: Object.keys(MATTER_TYPE_CONFIGS).filter(type => type !== 'Cogent Prep'),
    consultant: 'All',
    client: 'All',
    location: 'All',
    dateRange: {
      start: null,
      end: null,
    },
  };
};

export const useCalendarData = (enableApiCalls = true): UseCalendarDataReturn => {
  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const [filters, setFiltersState] = useState<CalendarFilters>(() => {
    const savedFilters = loadFiltersFromStorage();
    const defaultFilters = getDefaultFilters();

    if (savedFilters) {
      // Merge saved filters with defaults to ensure all properties exist
      return {
        ...defaultFilters,
        ...savedFilters,
        // Ensure matterType is an array and filter out any invalid types
        matterType: Array.isArray(savedFilters.matterType)
          ? savedFilters.matterType.filter(type => Object.keys(MATTER_TYPE_CONFIGS).includes(type))
          : defaultFilters.matterType,
      };
    }

    return defaultFilters;
  });

  // Request deduplication
  const requestInProgress = useRef<string | null>(null);
  const lastRequestTime = useRef<number>(0);

  // Load data
  const loadData = useCallback(async () => {
    if (!enableApiCalls) {
      return;
    }

    const startDate = getStartOfMonth(currentDate);
    const endDate = getEndOfMonth(currentDate);
    const requestKey = `${formatForApi(startDate)}-${formatForApi(endDate)}`;
    const now = Date.now();

    // Prevent duplicate requests (React strict mode)
    if (requestInProgress.current === requestKey) {
      // eslint-disable-next-line no-console
      console.log('Request already in progress, skipping duplicate');
      return;
    }

    // Rate limiting - prevent requests within 500ms
    if (now - lastRequestTime.current < 500) {
      // eslint-disable-next-line no-console
      console.log('Rate limiting active, skipping request');
      return;
    }

    requestInProgress.current = requestKey;
    lastRequestTime.current = now;
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      // Get metadata on first load to understand field structure
      if (matters.length === 0) {
        try {
          await bigtimeService.getReportMetadata();
        } catch (metadataError) {
          console.warn('Could not fetch report metadata:', metadataError);
        }
      }

      const data = await bigtimeService.getCalendarData(startDate, endDate);
      setMatters(data);
    } catch (err: any) {
      setIsError(true);
      setError(err);
      console.error('Error loading calendar data:', err);
    } finally {
      setIsLoading(false);
      requestInProgress.current = null;
    }
  }, [currentDate, enableApiCalls]);

  // Load data when date changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived data
  const allEvents = useMemo(() => generateEvents(matters), [matters]);

  // Pre-filter matters without consultant filter for better performance
  const mattersFilteredByNonConsultant = useMemo(() => {
    return filterMatters(matters, {
      matterType: filters.matterType.length > 0 ? filters.matterType : undefined,
      client: filters.client !== 'All' ? filters.client : undefined,
      location: filters.location !== 'All' ? filters.location : undefined,
    });
  }, [matters, filters.matterType, filters.client, filters.location]);

  const filteredMatters = useMemo(() => {
    if (filters.consultant === 'All') {
      return mattersFilteredByNonConsultant;
    }
    return filterMatters(mattersFilteredByNonConsultant, {
      consultant: filters.consultant,
    });
  }, [mattersFilteredByNonConsultant, filters.consultant]);

  // Cache events for pre-filtered matters to avoid regenerating when switching to "All Consultants"
  const eventsFromNonConsultantFiltered = useMemo(() =>
    generateEvents(mattersFilteredByNonConsultant),
    [mattersFilteredByNonConsultant]
  );

  const events = useMemo(() => {
    if (filters.consultant === 'All') {
      return eventsFromNonConsultantFiltered;
    }
    // For specific consultant, filter the pre-generated events
    return eventsFromNonConsultantFiltered.filter(event => {
      const matter = event.matter;
      const consultantMatch = [
        matter.leadConsultant,
        matter.secondaryConsultant,
        matter.technician,
        matter.trialPresentation,
      ].some(
        consultant =>
          consultant && consultant.toLowerCase().includes(filters.consultant!.toLowerCase())
      );
      return consultantMatch;
    });
  }, [eventsFromNonConsultantFiltered, filters.consultant]);

  const consultants = useMemo(() => {
    // Extract consultants from ALL events in the current calendar view (excluding consultant filter)
    // Use existing allEvents and filter them more efficiently

    // Get the date range for the current calendar display (including overflow days)
    const startOfMonth = getStartOfMonth(currentDate);
    const endOfMonth = getEndOfMonth(currentDate);

    // Extend range to include overflow days from previous/next months that appear in calendar
    const calendarStartDate = new Date(startOfMonth);
    calendarStartDate.setDate(calendarStartDate.getDate() - startOfMonth.getDay()); // Go back to Sunday

    const calendarEndDate = new Date(endOfMonth);
    const daysToAdd = 6 - endOfMonth.getDay(); // Go forward to Saturday
    calendarEndDate.setDate(calendarEndDate.getDate() + daysToAdd);

    // Use cached pre-filtered events for better performance
    const visibleEvents = eventsFromNonConsultantFiltered.filter(event =>
      event.date >= calendarStartDate && event.date <= calendarEndDate
    );

    // Extract unique matters from visible events
    const visibleMatters = Array.from(
      new Map(visibleEvents.map(event => [event.matter.id, event.matter])).values()
    );

    const consultantsFromVisibleEvents = extractConsultants(visibleMatters);

    return consultantsFromVisibleEvents;
  }, [eventsFromNonConsultantFiltered, currentDate]);

  // Separate memo for consultant list that includes selected consultant for persistence
  const consultantsWithSelected = useMemo(() => {
    const consultantSet = new Set(consultants);
    if (filters.consultant && filters.consultant !== 'All' && !consultantSet.has(filters.consultant)) {
      consultantSet.add(filters.consultant);
    }
    return Array.from(consultantSet).sort();
  }, [consultants, filters.consultant]);
  const clients = useMemo(() => extractClients(matters), [matters]);
  const locations = useMemo(() => extractLocations(matters), [matters]);

  // Actions
  const setFilters = useCallback((newFilters: Partial<CalendarFilters>) => {
    setFiltersState(prev => {
      const updatedFilters = { ...prev, ...newFilters };
      saveFiltersToStorage(updatedFilters);
      return updatedFilters;
    });
  }, []);

  const refreshData = useCallback(() => {
    loadData();
  }, [loadData]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }, []);

  const getEventsForDate = useCallback(
    (date: Date): CalendarEvent[] => {
      const dayEvents = events.filter(event => isSameDay(event.date, date));

      // Sort events according to requirements
      return dayEvents.sort((a, b) => {
        // First, sort by dateType priority
        const dateTypePriority: Record<string, number> = {
          'span': 1,
          'due': 2,
          'additional': 3,
          'trial': 4, // Not mentioned in requirements but included for completeness
        };

        const priorityA = dateTypePriority[a.dateType] || 999;
        const priorityB = dateTypePriority[b.dateType] || 999;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // For span events from the special case (due date before span date),
        // sort by earliest due date first
        if (a.dateType === 'span' && b.dateType === 'span') {
          const dueDateA = a.matter.dueDate ? parseDate(a.matter.dueDate) : null;
          const dueDateB = b.matter.dueDate ? parseDate(b.matter.dueDate) : null;
          const spanDateA = a.matter.spanDate ? parseDate(a.matter.spanDate) : null;
          const spanDateB = b.matter.spanDate ? parseDate(b.matter.spanDate) : null;

          // Check if both are from the special case (due date before span date)
          const isSpecialCaseA = dueDateA && spanDateA && dueDateA < spanDateA;
          const isSpecialCaseB = dueDateB && spanDateB && dueDateB < spanDateB;

          if (isSpecialCaseA && isSpecialCaseB && dueDateA && dueDateB) {
            const dateDiff = dueDateA.getTime() - dueDateB.getTime();
            if (dateDiff !== 0) {
              return dateDiff;
            }
          }
        }

        // Finally, sort by display name
        return a.matter.displayName.localeCompare(b.matter.displayName);
      });
    },
    [events]
  );

  const getMatterById = useCallback(
    (id: string): Matter | undefined => {
      return matters.find(matter => matter.id === id);
    },
    [matters]
  );

  return {
    // Data
    matters: filteredMatters,
    events,
    allEvents,
    filteredMatters,
    consultants: consultantsWithSelected,
    clients,
    locations,

    // State
    currentDate,
    selectedDate,
    selectedMatter,
    filters,

    // Loading states
    isLoading,
    isError,
    error,

    // Actions
    setCurrentDate,
    setSelectedDate,
    setSelectedMatter,
    setFilters,
    refreshData,
    goToToday,
    goToNextMonth,
    goToPreviousMonth,

    // Event helpers
    getEventsForDate,
    getMatterById,
  };
};
